/**
 * Tenant slug resolution for multi-tenant subdomain or query param.
 * - Subdomain: twentythreellc.getsocialbox.com, acme.localhost -> slug
 * - Query: ?tenant=twentythreellc (used for magic link redirect and join link)
 */

const TENANT_HOST_SUFFIXES = ['.localhost', '.127.0.0.1', '.thesocialbox.co', '.getsocialbox.com']

/**
 * Get tenant slug from current hostname (subdomain).
 * @param {string} [hostname] - Defaults to window.location.hostname
 * @returns {string|null} Slug or null
 */
export function getTenantSlugFromHost(hostname = typeof window !== 'undefined' ? window.location.hostname : '') {
  if (!hostname) return null
  const lower = hostname.toLowerCase()
  for (const suffix of TENANT_HOST_SUFFIXES) {
    if (lower.endsWith(suffix)) {
      const rest = lower.slice(0, -suffix.length)
      const parts = rest.split('.')
      const sub = parts.length > 0 ? parts[parts.length - 1] : ''
      if (sub && sub !== 'www') return sub
      return null
    }
  }
  return null
}

/**
 * Get tenant slug from URL search params (e.g. ?tenant=acme).
 * @param {URLSearchParams|string} [search] - searchParams or search string
 * @returns {string|null} Slug or null
 */
export function getTenantSlugFromSearch(search = null) {
  if (typeof window === 'undefined') return null
  const params = search != null
    ? (typeof search === 'string' ? new URLSearchParams(search) : search)
    : new URLSearchParams(window.location.search)
  const t = params.get('tenant')?.trim()
  return t || null
}

/**
 * Resolve tenant slug: query param first (for join/magic link), then host.
 * @param {URLSearchParams|string} [search] - Optional search params
 * @returns {string|null}
 */
export function resolveTenantSlug(search = null) {
  return getTenantSlugFromSearch(search) || getTenantSlugFromHost()
}

/**
 * Parse comma- or newline-separated emails into a trimmed, lowercased array.
 * @param {string} [input]
 * @returns {string[]}
 */
export function parseEmails(input) {
  if (!input || typeof input !== 'string') return []
  return input
    .split(/[\n,]+/)
    .map((e) => e.trim().toLowerCase())
    .filter(Boolean)
}

/** Super-admin tenant list: compact usage vs caps. */
export function formatTenantUsageLine(tenant) {
  const parts = []
  const clients = tenant?.clientCount ?? 0
  const creators = tenant?.influencerCount ?? 0
  if (tenant?.maxClients != null) {
    parts.push(`${clients}/${tenant.maxClients} clients`)
  } else if (clients > 0) {
    parts.push(`${clients} client${clients !== 1 ? 's' : ''}`)
  }
  if (tenant?.maxInfluencers != null) {
    parts.push(`${creators}/${tenant.maxInfluencers} creators`)
  } else if (creators > 0) {
    parts.push(`${creators} creator${creators !== 1 ? 's' : ''}`)
  }
  return parts.join(' · ')
}

export function formatUsageCap(current, max) {
  const maxLabel = max == null ? '∞' : max
  return `${current ?? 0} / ${maxLabel}`
}

export const ARCHIVE_BEFORE_TENANT_DELETE_MESSAGE =
  'Archive this workspace before you can delete it. Archiving blocks login. Permanent deletion is available after that.'

/** Permanent delete is only offered after the workspace has been archived. */
export function canPermanentlyDeleteTenant(status) {
  return String(status || '').toLowerCase() === 'archived'
}
